/**
 * 表 3：单 case × 类别明细（mock v3 最下一段），带 FOXGLOVE / 日志下钻。
 *
 * 下钻按钮全部以 ``taskId`` 为键——mcap / 日志的对象 key 都是 ``<taskId>/...`` 前缀，
 * ``sceneId`` 不够用。所以行里 ``taskId`` 为空时按钮禁用而不是拼一个必然 404 的 key。
 *
 * 每个 case 内的 ``ALL`` 行是该 case 的合计，后端已置底并在此加底纹。
 *
 * OD 报告里作为「分类别指标」的展开子表使用：隐藏冗余的类别列，并加任务级预览图列
 * （``previewUrlMap`` 由父组件按 taskId 批量换好签名 URL），预览后紧跟独立的 FOXGLOVE 列
 * （与任务列表同款胶囊按钮），此时「操作」列只留日志。
 */
import { useState } from 'react';
import { App, Button, Image, Space, Spin, Table, Tooltip } from 'antd';
import { FileTextOutlined, LinkOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import type { PerceptionCaseDetailRow } from '@/apis/simulation';
import ApiCodeLink from '@components/ApiCodeLink';
import { buildFoxgloveUrl, resolveMcapUrl } from '@/common/foxglove';
import { sceneGroupDisplay } from '@/constants/sceneGroup';

import { deltaColor, EMPTY, fmtCount, fmtPp, fmtRatio, isCellUnit } from '../format';

const ALL_CATEGORY = 'ALL';
const UNLABELED = '__unlabeled__';

const categoryLabel = (category: string, cn: string | null): string =>
  cn && cn !== category ? `${cn}(${category})` : category;

interface CaseDetailTableProps {
  rows: PerceptionCaseDetailRow[];
  /** 是否显示 Δ 召回率列（仅在选了基线 job 时有值） */
  showDelta: boolean;
  /** 打开 Task 日志 Modal（来自 useTaskLogModal） */
  onViewLog: (taskId: number) => void;
  /** 计数单位：cell 显示 IoU 列，其余显示平均 IoU 列 */
  unit?: string | null;
  /** 隐藏类别列（作为某一类别的展开子表时类别恒定，冗余） */
  hideCategory?: boolean;
  /** taskId → 任务级 preview.webp 签名 URL；传了才显示「预览」+「FOXGLOVE」列 */
  previewUrlMap?: Record<number, string>;
  /** 预览 URL 是否仍在批量获取中 */
  previewLoading?: boolean;
}

const PREVIEW_HEIGHT = 72;
/** 图片 404 / 签名过期时的占位（灰底文字 SVG，内联避免外部资源） */
const PREVIEW_FALLBACK =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="128" height="72"><rect width="100%" height="100%" fill="#f2f3f5"/><text x="50%" y="50%" fill="#9b9da3" font-size="12" text-anchor="middle" dominant-baseline="middle">暂无预览</text></svg>',
  );

export default function CaseDetailTable({
  rows,
  showDelta,
  onViewLog,
  unit,
  hideCategory = false,
  previewUrlMap,
  previewLoading = false,
}: CaseDetailTableProps) {
  const cell = isCellUnit(unit);
  const { message } = App.useApp();
  const [foxgloveLoadingKey, setFoxgloveLoadingKey] = useState<string | null>(null);

  const openFoxglove = async (row: PerceptionCaseDetailRow) => {
    if (!row.taskId || !row.caseId) return;
    const key = `${row.taskId}-${row.category}`;
    setFoxgloveLoadingKey(key);
    try {
      const url = await resolveMcapUrl(row.taskId, row.caseId);
      if (!url) {
        message.warning('未找到对应的 mcap 文件');
        return;
      }
      window.open(buildFoxgloveUrl(url, row.taskId), '_blank');
    } catch {
      message.error('获取 mcap 文件链接失败');
    } finally {
      setFoxgloveLoadingKey(null);
    }
  };

  const columns: ColumnsType<PerceptionCaseDetailRow> = [
    {
      title: 'Case',
      dataIndex: 'caseId',
      key: 'caseId',
      width: 160,
      fixed: 'left',
      render: (v: string | null) => v || EMPTY,
    },
    ...(previewUrlMap
      ? [
          {
            title: '预览',
            key: 'preview',
            width: 150,
            render: (_: unknown, r: PerceptionCaseDetailRow) => {
              const url = r.taskId ? previewUrlMap[r.taskId] : undefined;
              if (!url) {
                return (
                  <span style={{ color: '#9b9da3', fontSize: 12 }}>
                    {previewLoading ? '加载中…' : '暂无预览'}
                  </span>
                );
              }
              return (
                <Image
                  src={url}
                  height={PREVIEW_HEIGHT}
                  loading="lazy"
                  style={{ objectFit: 'contain', maxWidth: 130 }}
                  fallback={PREVIEW_FALLBACK}
                />
              );
            },
          },
          {
            title: 'FOXGLOVE',
            key: 'foxglove',
            width: 120,
            render: (_: unknown, r: PerceptionCaseDetailRow) => {
              const loadingThis = foxgloveLoadingKey === `${r.taskId}-${r.category}`;
              const disabled = !r.taskId || !r.caseId || loadingThis;
              return (
                <Tooltip
                  title={disabled && !loadingThis ? '暂无可回放数据' : '在 Foxglove 中打开回放'}
                >
                  <button
                    type="button"
                    className="pe-fox-link-btn"
                    disabled={disabled}
                    onClick={() => openFoxglove(r)}
                  >
                    {loadingThis ? <Spin size="small" /> : <LinkOutlined />}
                    foxglove
                  </button>
                </Tooltip>
              );
            },
          },
        ]
      : []),
    ...(hideCategory
      ? []
      : [
          {
            title: '类别',
            dataIndex: 'category',
            key: 'category',
            width: 150,
            render: (_: unknown, r: PerceptionCaseDetailRow) =>
              categoryLabel(r.category, r.categoryCn),
          },
        ]),
    {
      title: 'API_CODE',
      dataIndex: 'apiCode',
      key: 'apiCode',
      width: 130,
      render: (v: string | null, r) => <ApiCodeLink code={v} source={r.source} />,
    },
    {
      title: '场景分组',
      dataIndex: 'sceneGroup',
      key: 'sceneGroup',
      width: 110,
      render: (v: string | null) => sceneGroupDisplay(v),
    },
    {
      title: '场景标签',
      dataIndex: 'label',
      key: 'label',
      width: 150,
      ellipsis: true,
      render: (v: string | null) => (!v || v === UNLABELED ? EMPTY : v),
    },
    { title: 'GT', dataIndex: 'gt', key: 'gt', width: 80, align: 'right', render: fmtCount },
    { title: 'TP', dataIndex: 'tp', key: 'tp', width: 80, align: 'right', render: fmtCount },
    { title: 'FN', dataIndex: 'fn', key: 'fn', width: 80, align: 'right', render: fmtCount },
    { title: 'FP', dataIndex: 'fp', key: 'fp', width: 80, align: 'right', render: fmtCount },
    {
      title: '召回率',
      dataIndex: 'recall',
      key: 'recall',
      width: 95,
      align: 'right',
      sorter: (a, b) => (a.recall ?? -1) - (b.recall ?? -1),
      render: (v) => fmtRatio(v),
    },
    {
      title: cell ? 'IoU' : '平均 IoU',
      dataIndex: cell ? 'iou' : 'meanIou',
      key: 'iou',
      width: 95,
      align: 'right',
      render: (v: number | null | undefined) => fmtRatio(v),
    },
    ...(showDelta
      ? [
          {
            title: 'Δ 召回率',
            dataIndex: 'deltaRecall',
            key: 'deltaRecall',
            width: 105,
            align: 'right' as const,
            sorter: (a: PerceptionCaseDetailRow, b: PerceptionCaseDetailRow) =>
              (a.deltaRecall ?? 0) - (b.deltaRecall ?? 0),
            render: (v: number | null) => (
              <span style={{ color: deltaColor(v) }}>{fmtPp(v)}</span>
            ),
          },
        ]
      : []),
    {
      title: '操作',
      key: 'action',
      width: previewUrlMap ? 70 : 130,
      fixed: 'right',
      render: (_, r) => {
        const disabled = !r.taskId;
        const key = `${r.taskId}-${r.category}`;
        return (
          <Space size={2}>
            {/* 有独立 FOXGLOVE 列时这里不再重复放回放入口 */}
            {!previewUrlMap && (
              <Tooltip title={disabled ? '该场景无 task，无法回放' : 'FOXGLOVE 可视化回放'}>
                <Button
                  type="text"
                  size="small"
                  icon={<LinkOutlined />}
                  disabled={disabled}
                  loading={foxgloveLoadingKey === key}
                  onClick={() => openFoxglove(r)}
                />
              </Tooltip>
            )}
            <Tooltip title={disabled ? '该场景无 task，无日志' : 'Task 日志'}>
              <Button
                type="text"
                size="small"
                icon={<FileTextOutlined />}
                disabled={disabled}
                onClick={() => r.taskId && onViewLog(r.taskId)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Table<PerceptionCaseDetailRow>
      rowKey={(r) => `${r.sceneId}-${r.category}`}
      size="small"
      dataSource={rows}
      columns={columns}
      tableLayout="fixed"
      scroll={{ x: 1480 + (previewUrlMap ? 210 : 0) - (hideCategory ? 150 : 0) }}
      // 作为单一类别的展开子表时类别恒定，ALL 底纹失去区分意义（展开 ALL 会整片发灰），不加
      rowClassName={(r) => (!hideCategory && r.category === ALL_CATEGORY ? 'pe-row-all' : '')}
      pagination={{
        defaultPageSize: 20,
        showSizeChanger: true,
        pageSizeOptions: ['20', '50', '100', '500'],
        showQuickJumper: true,
        showTotal: (t) => `总共 ${t} 行`,
      }}
    />
  );
}
